/**
 * Tab Component
 *
 * A flexible tab component for navigation or content switching.
 * Supports multiple types: normal, icon, attention (with notification dot), and badge.
 *
 * States: default, hover, selected, disabled
 *
 * @example
 * // Basic usage with TabGroup
 * <TabGroup>
 *   <Tab text="Tab 1" selected />
 *   <Tab text="Tab 2" />
 *   <Tab text="Tab 3" />
 * </TabGroup>
 *
 * @example
 * // With left icon
 * import HomeIcon from 'src/assets/home.svg';
 * <Tab text="Home" leftIcon={HomeIcon} />
 *
 * @example
 * // With attention dot (notification indicator)
 * <Tab text="Messages" showAttention />
 *
 * @example
 * // With badge
 * <Tab text="Inbox" badgeText="12" />
 *
 * @example
 * // With custom badge props
 * <Tab text="Alerts" badgeText="3" chipProps={{ colorScheme: 'error' }} />
 *
 * @example
 * // Controlled with value
 * const [value, setValue] = useState('tab1');
 * <TabGroup value={value} onValueChange={setValue}>
 *   <Tab text="Tab 1" value="tab1" />
 *   <Tab text="Tab 2" value="tab2" />
 *   <Tab text="Tab 3" value="tab3" />
 * </TabGroup>
 */
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'src/utils/utils';
import { Typography } from './typography';
import { Icon, type IconProps } from './icon';
import { Chip, type ChipProps } from './chip';

const tabVariants = cva(
  `
    tw:inline-flex
    tw:justify-center
    tw:items-center
    tw:gap-s
    tw:py-2xs
    tw:px-s
    tw:min-w-4xl

    tw:rounded-full
    
    tw:whitespace-nowrap
    
    tw:cursor-pointer
    tw:select-none
    
    tw:transition-colors
    tw:duration-150
  `,
  {
    variants: {
      state: {
        default: `
          tw:bg-transparent
        `,
        hover: `
          tw:bg-transparent
        `,
        selected: `
          tw:bg-tertiary
        `,
        disabled: `
          tw:bg-transparent
          tw:cursor-not-allowed
        `
      }
    },
    defaultVariants: {
      state: 'default'
    }
  }
);

/**
 * Get text color class based on state
 */
const getTextColorClass = (
  state: 'default' | 'hover' | 'selected' | 'disabled' | null | undefined
): string => {
  switch (state) {
    case 'hover':
    case 'selected':
      return 'tw:text-text-primary';
    case 'disabled':
      return 'tw:text-text-disabled';
    case 'default':
    default:
      return 'tw:text-text-secondary';
  }
};

/**
 * Get icon color class based on state
 */
const getIconColorClass = (
  state: 'default' | 'hover' | 'selected' | 'disabled' | null | undefined
): string => {
  switch (state) {
    case 'hover':
    case 'selected':
      return 'tw:text-icon-primary-hover';
    case 'disabled':
      return 'tw:text-icon-disabled';
    case 'default':
    default:
      return 'tw:text-icon-primary';
  }
};

export interface TabProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'>,
    VariantProps<typeof tabVariants> {
  /**
   * Text content for the tab.
   * If children are provided, this prop is ignored.
   */
  text?: string;

  /**
   * Value of this tab (used with TabGroup).
   */
  value?: string;

  /**
   * Whether the tab is currently selected.
   */
  selected?: boolean;

  /**
   * Icon to display on the left side.
   *
   * @example
   * import HomeIcon from 'src/assets/home.svg';
   * <Tab text="Home" leftIcon={HomeIcon} />
   */
  leftIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;

  /**
   * Props to pass to the left Icon component.
   */
  leftIconProps?: Omit<IconProps, 'icon'>;

  /**
   * Show attention dot (notification indicator).
   * Displays a small dot next to the text.
   *
   * @default false
   */
  showAttention?: boolean;

  /**
   * Custom color class for the attention dot.
   * Use Tailwind background color classes.
   *
   * @default 'bg-brand'
   *
   * @example
   * <Tab text="Errors" showAttention attentionColorClass="bg-error-dark" />
   * <Tab text="Warnings" showAttention attentionColorClass="bg-warning-dark" />
   */
  attentionColorClass?: string;

  /**
   * Badge text to display.
   * When provided, shows a badge chip next to the text.
   * This is a shorthand for chipProps.text.
   */
  badgeText?: string;

  /**
   * Props to pass to the Chip component for the badge.
   * Use this for full control over the badge appearance.
   *
   * @example
   * // Custom badge with different color scheme
   * <Tab
   *   text="Messages"
   *   badgeText="5"
   *   chipProps={{ colorScheme: 'error', variant: 'filled' }}
   * />
   *
   * @example
   * // Badge with icons
   * import StarIcon from 'src/assets/star.svg';
   * <Tab
   *   text="Featured"
   *   chipProps={{ text: 'New', leftIcon: StarIcon, colorScheme: 'success' }}
   * />
   */
  chipProps?: Omit<ChipProps, 'children'>;
}

// Context for TabGroup
interface TabContextValue {
  value?: string;
  onValueChange?: (value: string) => void;
}

const TabContext = React.createContext<TabContextValue>({});

const useTabContext = () => React.useContext(TabContext);

const Tab = React.forwardRef<HTMLButtonElement, TabProps>(
  (
    {
      className,
      text,
      value,
      selected: selectedProp,
      disabled,
      leftIcon,
      leftIconProps,
      showAttention = false,
      attentionColorClass = 'tw:bg-brand',
      badgeText,
      chipProps,
      onClick,
      children,
      ...props
    },
    ref
  ) => {
    const context = useTabContext();
    const [isHovered, setIsHovered] = React.useState(false);

    // Determine if selected from prop or context
    const isSelected =
      selectedProp ?? (value !== undefined && context.value === value);

    // Determine state
    const getState = (): 'default' | 'hover' | 'selected' | 'disabled' => {
      if (disabled) return 'disabled';
      if (isSelected) return 'selected';
      if (isHovered) return 'hover';
      return 'default';
    };

    const state = getState();
    const textColorClass = getTextColorClass(state);
    const iconColorClass = getIconColorClass(state);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;

      if (value !== undefined && context.onValueChange) {
        context.onValueChange(value);
      }

      onClick?.(e);
    };

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          tabVariants({ state }),
          // Add hover styles
          !disabled && !isSelected && 'tw:hover:bg-transparent',
          className
        )}
        disabled={disabled}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-selected={isSelected}
        role="tab"
        {...props}
      >
        {children || (
          <>
            {leftIcon && (
              <Icon
                icon={leftIcon}
                sizeClass="tw:h-l tw:w-l"
                colorClass={iconColorClass}
                {...leftIconProps}
              />
            )}
            {text && (
              <Typography variant="h5" colorClass={textColorClass}>
                {text}
              </Typography>
            )}
            {showAttention && (
              <span
                className={cn(
                  'tw:w-s tw:h-s tw:rounded-full',
                  attentionColorClass
                )}
                aria-label="Attention indicator"
              />
            )}
            {(badgeText || chipProps) && (
              <Chip
                type="badge"
                variant="filled"
                colorScheme="brand"
                text={badgeText}
                {...chipProps}
              />
            )}
          </>
        )}
      </button>
    );
  }
);

Tab.displayName = 'Tab';

/**
 * TabGroup Component
 *
 * A wrapper component for grouping Tab components.
 * Provides context for value-based selection.
 *
 * @example
 * const [value, setValue] = useState('tab1');
 * <TabGroup value={value} onValueChange={setValue}>
 *   <Tab text="Tab 1" value="tab1" />
 *   <Tab text="Tab 2" value="tab2" />
 *   <Tab text="Tab 3" value="tab3" />
 * </TabGroup>
 */
export interface TabGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Currently selected value.
   */
  value?: string;

  /**
   * Callback when selection changes.
   */
  onValueChange?: (value: string) => void;
}

const TabGroup = React.forwardRef<HTMLDivElement, TabGroupProps>(
  ({ className, value, onValueChange, children, ...props }, ref) => {
    return (
      <TabContext.Provider value={{ value, onValueChange }}>
        <div
          ref={ref}
          role="tablist"
          className={cn('tw:inline-flex tw:gap-2xs', className)}
          {...props}
        >
          {children}
        </div>
      </TabContext.Provider>
    );
  }
);

TabGroup.displayName = 'TabGroup';

export { Tab, TabGroup, tabVariants };
